import React, { useState, useEffect } from "react";

import PanelTemplate from "../../templates/Panel";
import InputSelect from "../../components/InputSelect";
import Input from "../../components/Input";
import api from "../../services/api";
import { useParams } from "react-router-dom";
import Box from "../../components/Box";
import PageFooter from "../../components/PageFooter";
import Button from "../../components/Button";
import { renderError } from "../../helpers/errors";
import InputEditor from "../../components/InputEditor";
import { Loading } from "../../components/Loading";
import InputTextarea from "../../components/InputTextarea";
import InputCheckboxUnique from "../../components/InputCheckboxUnique";
import { toast } from "react-toastify";
import CoverField, { SIGNATURE } from "./CoverField";

export const EmailTemplateEdit = () => {
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [emailTemplate, setEmailTemplate] = useState(null);

    const [type, setType] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [comments, setComments] = useState('');
    // Só o PDF do orçamento tem o link "Mais detalhes" nos produtos.
    const [showProductLink, setShowProductLink] = useState(true);
    const [coverUrl, setCoverUrl] = useState(null);
    const [coverName, setCoverName] = useState('');
    const [coverFile, setCoverFile] = useState(null);
    const [coverRemoved, setCoverRemoved] = useState(false);
    const [signatureUrl, setSignatureUrl] = useState(null);
    const [signatureName, setSignatureName] = useState('');
    const [signatureFile, setSignatureFile] = useState(null);
    const [signatureRemoved, setSignatureRemoved] = useState(false);
    const [bccResponsible, setBccResponsible] = useState(false);
    const [bccCustom, setBccCustom] = useState(false);
    const [bccCustomEmail, setBccCustomEmail] = useState('');
    const [bccCustomEmailError, setBccCustomEmailError] = useState('');
    const isBudget = type === 'budget';
    const documentName = isBudget ? 'orçamento' : 'pedido';

    useEffect(() => {
        getEmailTemplate();
    }, []);

    const getEmailTemplate = () => {
        setLoading(true);

        api.get(`/email-template/${id}`).then(res => {
            let resource = res.data.resource;

            setEmailTemplate(resource);
            setType(resource.type);
            setSubject(resource.subject ?? '');
            setBody(resource.body ?? '');
            setComments(resource.comments ?? '');
            setShowProductLink(resource.show_product_link ?? true);
            setCoverUrl(resource.cover_url ?? null);
            setCoverName(resource.cover_file ?? '');
            setCoverFile(null);
            setCoverRemoved(false);
            setSignatureUrl(resource.signature_url ?? null);
            setSignatureName(resource.signature_file ?? '');
            setSignatureFile(null);
            setSignatureRemoved(false);
            setBccResponsible(resource.bcc_responsible ?? false);
            setBccCustom(resource.bcc_custom ?? false);
            setBccCustomEmail(resource.bcc_custom_email ?? '');
            setBccCustomEmailError('');
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const updateTemplateEmail = () => {
        setLoading(true);

        // FormData porque capa e assinatura são arquivos; `_method` mantém a rota PUT.
        let form = new FormData();

        form.append('_method', 'PUT');
        form.append('type', type);
        form.append('subject', subject);
        form.append('body', body);
        form.append('comments', comments ?? '');
        form.append('show_product_link', showProductLink ? '1' : '0');
        form.append('bcc_responsible', bccResponsible ? '1' : '0');
        form.append('bcc_custom', bccCustom ? '1' : '0');
        form.append('bcc_custom_email', bccCustomEmail ?? '');

        if(coverFile){
            form.append('cover', coverFile);
        } else if(coverRemoved){
            form.append('cover_remove', '1');
        }

        if(signatureFile){
            form.append('signature', signatureFile);
        } else if(signatureRemoved){
            form.append('signature_remove', '1');
        }

        api.post(`/email-template/${id}`, form).then(res => {
            getEmailTemplate();
            toast.success('Modelo atualizado com sucesso');
        }).catch(error => {
            setBccCustomEmailError(error.response?.data?.fields?.bcc_custom_email?.[0] ?? '');
            renderError(error);
            setLoading(false);
        });
    }

    return (
        <>
            <PanelTemplate id="email-template-edit">
                <Box>
                    {!loading &&
                        <>
                            <Input label={`Assunto`} value={subject} change={setSubject} />
                            <h2>Instruções para o corpo do e-mail</h2>
                            <p>Você pode usar [nome_do_contato] para exibir o nome do contato.<br />
                            Você pode usar [codigo] para exibir o código.<br />
                            Você pode usar [nome_usuario] para exibir o nome do usuário responsável.<br />
                            Você pode usar [email_usuario] para exibir o nome do usuário responsável.<br />
                            Você pode usar [telefone_usuario] para exibir o telefone do usuário responsável.<br />
                            </p>
                            <InputTextarea label={`Corpo do e-mail`} value={body} change={setBody} rows={6} />
                            <h2>Instruções para as observações no PDF</h2>
                            <p>Este texto aparecerá na última página do PDF do {documentName}.</p>
                            <InputTextarea label={`Observações no PDF do ${documentName}`} value={comments} change={setComments} rows={10} />

                            <h2>Assinatura e carimbo</h2>
                            <p>
                                Entra no final do PDF do {documentName}, depois das observações, alinhada à direita.
                                Use uma imagem só com a assinatura e o carimbo do CNPJ.
                            </p>
                            <CoverField
                                config={SIGNATURE}
                                savedUrl={signatureUrl}
                                savedName={signatureName}
                                removed={signatureRemoved}
                                file={signatureFile}
                                onSelect={setSignatureFile}
                                onCancel={() => setSignatureFile(null)}
                                onRemove={() => setSignatureRemoved(true)}
                                onUndoRemove={() => setSignatureRemoved(false)}
                            />
                            {isBudget &&
                                <>
                                    <h2>Link do produto no PDF</h2>
                                    <p>Quando marcado, cada produto do PDF do orçamento exibe um link para a página do produto no site.</p>
                                    <InputCheckboxUnique
                                        label={`Exibir link "Mais detalhes" no PDF do orçamento`}
                                        checked={showProductLink}
                                        change={setShowProductLink}
                                    />

                                    <h2>Cópias do e-mail do orçamento</h2>
                                    <p>Quando marcado, os destinatários abaixo recebem uma cópia oculta (CCO) do e-mail enviado ao cliente. O cliente não vê quem está em cópia.</p>
                                    <InputCheckboxUnique
                                        label={`Enviar cópia para o responsável do orçamento`}
                                        checked={bccResponsible}
                                        change={setBccResponsible}
                                    />
                                    <InputCheckboxUnique
                                        label={`Enviar cópia para um e-mail específico`}
                                        checked={bccCustom}
                                        change={setBccCustom}
                                    />
                                    {bccCustom &&
                                        <div className="bcc-custom-email">
                                            <Input
                                                type={`email`}
                                                label={`E-mail para cópia`}
                                                value={bccCustomEmail}
                                                change={value => {
                                                    setBccCustomEmail(value);
                                                    setBccCustomEmailError('');
                                                }}
                                                error={bccCustomEmailError}
                                                hint={`Vai em cópia oculta: o cliente não verá este endereço.`}
                                            />
                                        </div>
                                    }

                                    <h2>Capa da proposta</h2>
                                    <p>
                                        Entra antes da primeira página do PDF do orçamento, como apresentação da empresa.
                                    </p>
                                    <CoverField
                                        savedUrl={coverUrl}
                                        savedName={coverName}
                                        removed={coverRemoved}
                                        file={coverFile}
                                        onSelect={setCoverFile}
                                        onCancel={() => setCoverFile(null)}
                                        onRemove={() => setCoverRemoved(true)}
                                        onUndoRemove={() => setCoverRemoved(false)}
                                    />
                                </>
                            }
                        </>
                    }
                    {loading &&
                        <Loading />
                    }
                </Box>
                <PageFooter>
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`save-white`}
                        text={`Salvar alterações`}
                        action={updateTemplateEmail}
                        loading={loading}
                    />
                </PageFooter>
            </PanelTemplate>
        </>
    );
}

export default EmailTemplateEdit;
